import React from "react";
import { WidgetData } from "./Widgets";

export interface ClassificationColorMap {
  [key: string]: string;
}

// define a color mapping for rcgrn1, rcylw1, rcred1
export const colorMap: ClassificationColorMap = {
  rcgrn1: "#3F9533",
  rcylw1: "#FC3",
  rcred1: "#FF7A7A",
};

export interface AssessmentOutcomesGraphData {
  period: string; // "Jan", "Feb", etc.
  performanceScaleStats: {
    label: string; // "Level 1", "Level 2", etc.
    value: string; // "level1", "level2", etc.
    count: number; // number of students in that level
  }[];
}

export interface AssessmentOutcomesLevelData {
  label: {
    labelEN: string; // English label
    labelES: string; // Spanish label
  };
  value: string; // level-value
  color: string; // color associated with level-value
}

export const TICK = "tick";

export const TRANSLATE_SECTION = "teacher_report_chart_progress";

export const dottedLineSvg = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 2 157" fill="none">
    <path
      d="M1.375 20L1.37499 156"
      stroke="black"
      strokeOpacity="0.5"
      strokeLinecap="round"
      strokeDasharray="4 6"
    />
  </svg>
);

export interface AssessmentOutcomesGraphDataTransformed {
  label: string; // e.g., "Jan", "Feb"
  data: {
    label: string; // e.g., "Tier 1", "Tier 2"
    color: string; // e.g., "#FF7A7A"
    value: number; // e.g., 14
  }[];
}

export type AssessmentOutcomesWidgetModelInterface = WidgetData & {
  graphData: AssessmentOutcomesGraphDataTransformed[]; // Updated to use the new interface
  levels: AssessmentOutcomesLevelData[];
  title?: string;
  width?: string | number;
  height?: string | number;
  barWidth?: number;
  id?: string;
};

export type AssessmentOutcomesAPIResponseInterface = {
  classroomAssessmentOutcomeData: {
    graphData: AssessmentOutcomesGraphData[];
    configsUsed: {
      cutlineLabel: string;
      cutlineType: string;
      levels: AssessmentOutcomesLevelData[];
    }
  };
};

export class AssessmentOutcomesWidgetModel implements AssessmentOutcomesWidgetModelInterface {
  [key: string]: unknown;
  graphData: AssessmentOutcomesGraphDataTransformed[];
  levels: AssessmentOutcomesLevelData[];
  title?: string;
  width?: string | number | undefined;
  height?: string | number | undefined;
  barWidth?: number | undefined;
  id?: string | undefined;

  constructor(
    outcomesResponseData: AssessmentOutcomesAPIResponseInterface,
  ) {
    const { graphData, configsUsed } = outcomesResponseData.classroomAssessmentOutcomeData;

    this.graphData = graphData.map((data) => ({
      label: data.period,
      data: data.performanceScaleStats.map((stat) => {
        const levelConfig = configsUsed.levels.find((level) => level.value === stat.value);
        return {
          label: levelConfig?.label.labelEN || stat.label,
          color: levelConfig ? colorMap[levelConfig.color] : "#000",
          value: stat.count,
        };
      }),
    }));

    this.levels = configsUsed.levels.map((level) => ({
      ...level,
      value: level.value.replace(/_/g, ""),
    }));

    this.title = configsUsed.cutlineLabel;

    // Default values
    this.width = "100%";
    this.height = 248;
    this.barWidth = 32;
    this.id = "assessment-outcomes-widget";
  }
}

export const mockedClassificationResponse: AssessmentOutcomesAPIResponseInterface = {
  classroomAssessmentOutcomeData: {
    graphData: [
      {
        period: "Jan",
        performanceScaleStats: [
          {
            label: "Level 3",
            value: "level3",
            count: 8
          },
          {
            label: "Level 1",
            value: "level1",
            count: 10
          },
          {
            label: "Level 2",
            value: "level2",
            count: 20
          }
        ]
      },
      {
        period: "Feb",
        performanceScaleStats: [
          {
            label: "Level 3",
            value: "level3",
            count: 11
          },
          {
            label: "Level 1",
            value: "level1",
            count: 15
          },
          {
            label: "Level 2",
            value: "level2",
            count: 25
          },
        ]
      },
    ],
    configsUsed: {
      cutlineLabel: "Cutline Label Example",
      cutlineType: "Cutline Type Example",
      levels: [
        {
          label: {
            labelEN: "Level 1",
            labelES: "Nivel 1"
          },
          value: "level1",
          color: "rcgrn1"
        },
        {
          label: {
            labelEN: "Level 2",
            labelES: "Nivel 2"
          },
          value: "level2",
          color: "rcylw1"
        },
        {
          label: {
            labelEN: "Level 3",
            labelES: "Nivel 3"
          },
          value: "level3",
          color: "rcred1"
        }
      ]
    }
  }
};

